/**
 * NotificationDropdown Component
 * Mobile: Bottom sheet with backdrop. Desktop: Dropdown from bell.
 * Uses compact NotificationCard for rich notification rendering.
 */

import type { Notification } from '@parlay-picker/shared/notification';
import { NotificationCard } from '@views/feed/components/NotificationCard';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCheck, Loader2, X } from 'lucide-react';
import { type ReactElement, type RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import LoadingSpinner from './LoadingSpinner';

// =============================================================================
// Types
// =============================================================================

interface NotificationDropdownProps {
  notifications: Notification[];
  loading: boolean;
  hasMore?: boolean;
  onLoadMore?: () => Promise<void>;
  onMarkAsRead: (ids: string[]) => Promise<void>;
  onMarkAllAsRead: () => Promise<void>;
  onDismiss: (id: string) => Promise<void>;
  onClose: () => void;
  onNavigateToParlay?: (parlayId: number) => void;
  /** The bell button, used to anchor the desktop dropdown (portal has no positioned parent). */
  anchorRef: RefObject<HTMLElement | null>;
}

const DESKTOP_MQ = '(min-width: 768px)';

// =============================================================================
// Component
// =============================================================================

export function NotificationDropdown({
  notifications,
  loading,
  hasMore = false,
  onLoadMore,
  onMarkAsRead,
  onMarkAllAsRead,
  onDismiss,
  onClose,
  onNavigateToParlay,
  anchorRef,
}: NotificationDropdownProps): ReactElement | null {
  const hasUnread = notifications.some((n) => !n.readAt);
  const [loadingMore, setLoadingMore] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Rendered in a portal on document.body so `position: fixed` resolves against
  // the viewport. Inside the sticky header (backdrop-filter) and the @container
  // main region (container-type), fixed would otherwise anchor to those boxes
  // and the mobile bottom sheet rendered off-screen at the top.
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(DESKTOP_MQ).matches,
  );
  const [desktopPos, setDesktopPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(DESKTOP_MQ);
    const onChange = (): void => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Desktop: anchor to the bell (portal has no positioned ancestor for md:absolute).
  useEffect(() => {
    if (!isDesktop) return;
    const update = (): void => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDesktopPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isDesktop, anchorRef]);

  // Escape + outside-click. Portal-safe: the sheet is no longer a DOM child of
  // the bell, so check both the sheet and the anchor (bell toggles itself).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (sheetRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, anchorRef]);

  const handleLoadMore = async () => {
    if (!onLoadMore || loadingMore) return;
    setLoadingMore(true);
    try {
      await onLoadMore();
    } finally {
      setLoadingMore(false);
    }
  };

  if (typeof document === 'undefined') return null;

  const overlay = (
    <AnimatePresence>
      {/* Backdrop (mobile only) */}
      <motion.div
        data-testid="notification-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 md:hidden"
        onClick={onClose}
      />

      {/* Desktop: anchored panel near the bell. Mobile: bottom sheet. */}
      <motion.div
        ref={sheetRef}
        initial={isDesktop ? { opacity: 0, y: -8 } : { y: '100%', opacity: 0.8 }}
        animate={{ y: 0, opacity: 1 }}
        exit={isDesktop ? { opacity: 0, y: -8 } : { y: '100%', opacity: 0 }}
        transition={
          isDesktop ? { duration: 0.15 } : { type: 'spring', stiffness: 400, damping: 35 }
        }
        style={
          isDesktop && desktopPos ? { top: desktopPos.top, right: desktopPos.right } : undefined
        }
        className={
          isDesktop
            ? 'fixed z-50 w-96 max-w-[calc(100vw-1rem)] bg-theme-surface border border-theme-border rounded-xl shadow-xl overflow-hidden'
            : 'fixed inset-x-0 bottom-0 z-50 w-full bg-theme-surface border border-theme-border rounded-t-2xl shadow-xl overflow-hidden'
        }
      >
        {/* Drag handle (mobile only) */}
        <div className="flex justify-center pt-2 pb-0 md:hidden">
          <div className="w-10 h-1 rounded-full bg-theme-surface-active" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-theme-border">
          <h3 className="text-sm font-semibold text-theme-text">Notifications</h3>
          <div className="flex items-center gap-2">
            {hasUnread && (
              <button
                type="button"
                data-testid="notification-mark-all-read"
                onClick={onMarkAllAsRead}
                className="text-xs text-theme-primary hover:text-theme-primary-hover transition-colors flex items-center gap-1 min-h-11 min-w-11 justify-center"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Mark all read</span>
              </button>
            )}
            <button
              type="button"
              data-testid="notification-dropdown-close"
              aria-label="Close notifications"
              onClick={onClose}
              className="p-1 rounded hover:bg-theme-surface-hover text-theme-text-tertiary transition-colors min-h-11 min-w-11 flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-96 overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center text-sm text-theme-text-tertiary">
              No notifications
            </div>
          ) : (
            <div className="p-2 space-y-1.5">
              {notifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={onMarkAsRead}
                  onDismiss={onDismiss}
                  onNavigateToParlay={onNavigateToParlay}
                  compact
                />
              ))}
              {hasMore && onLoadMore && (
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-theme-primary hover:text-theme-primary-hover transition-colors disabled:opacity-50 min-h-9"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load more'
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(overlay, document.body);
}
